// ─────────────────────────────────────────────────────────────────────────
// useDuplicateCheck — "have we got this person already?", asked at the moment
// it is useful.
//
// WHEN IT RUNS
//   • right after a résumé has been read and the form filled in, because that
//     is the first time an email and phone number exist to check;
//   • again on submit, as the authoritative gate.
//
// WHAT IT SHOWS
// A dialog naming the existing record and how long ago it was touched, with the
// two things a user can actually do: open that record and update it (the new
// file is kept as another version), or go back and look again. Every word comes
// from the module's own config — see components/form/duplicateRecord.js.
//
// It FAILS OPEN. A check that cannot run (offline, a backend that predates the
// endpoint, a module with no stages configured) must never stop someone
// creating a legitimate record.
// ─────────────────────────────────────────────────────────────────────────
import { useCallback, useRef, useState } from 'react';
import { checkDuplicateRecord } from '../../services/uniqueValidationApi';
import { duplicateMessage, duplicateBlocks, duplicateFacts } from './duplicateRecord';
import { openFormDecision, openFormNotice } from './formDecisionDialog';

/**
 * @param {object}   opts
 * @param {object}   opts.form        the antd form instance
 * @param {string}   opts.moduleName  module being created
 * @param {string}  [opts.recordId]   set on EDIT, so a record never matches itself
 * @param {object}  [opts.config]     { message, policy, displayFields } fallback
 * @param {function}[opts.onUseExisting] called with the matched id when the user
 *                                       chooses to update the existing record
 */
export default function useDuplicateCheck({
  form,
  moduleName,
  recordId,
  config = {},
  onUseExisting,
} = {}) {
  const [checking, setChecking] = useState(false);
  // Remembers a match the user has already accepted, so submitting after
  // choosing "update the existing one" does not ask the same question twice.
  const acknowledged = useRef(null);

  /**
   * check — ask the backend.
   *
   * `payload` overrides the form's own values. That matters right after a
   * résumé is read: the parsed email and phone exist in the RESPONSE and are
   * not on the form yet, so checking the form would ask about a blank page.
   */
  const check = useCallback(async (payload) => {
    if (!moduleName) return { duplicate: false };
    setChecking(true);
    try {
      return await checkDuplicateRecord({
        module: moduleName,
        payload: payload ?? form.getFieldsValue(true) ?? {},
        excludeId: recordId,
      });
    } finally {
      setChecking(false);
    }
  }, [form, moduleName, recordId]);

  /**
   * prompt — run the check and, if something matches, ask.
   *
   * Resolves to:
   *   'clear'    nothing matched — carry on
   *   'existing' the user chose to update the record we already have
   *   'blocked'  the module is set to refuse, or the user backed out
   */
  const prompt = useCallback(async ({ reason, payload, carry } = {}) => {
    const result = await check(payload);
    if (!result?.duplicate) {
      acknowledged.current = null;
      return 'clear';
    }
    if (acknowledged.current && acknowledged.current === result.id) return 'existing';

    const body = duplicateMessage(result, config);
    // The two things that actually decide the answer — WHO matched and HOW OLD
    // that record is — are shown as facts rather than left inside the sentence.
    const facts = duplicateFacts(result, config.displayFields);
    // A stage-1 hit (email or phone) is near proof; a stage-2 hit (name only)
    // is a maybe. Saying which stops both being read with the same weight.
    const heading = result.weight === 'weak'
      ? 'This might be someone you already have'
      : 'You already have this person on file';

    if (duplicateBlocks(result, config)) {
      await openFormNotice({ tone: 'duplicate', title: heading, facts, body });
      return 'blocked';
    }

    const useExisting = await openFormDecision({
      tone: 'duplicate',
      title: heading,
      facts,
      body,
      // Naming the OUTCOMES rather than "OK"/"Cancel": the choice is between
      // two different results, and neither is obviously the default.
      okText: 'Open their record',
      cancelText: 'Let me check first',
      // Says what happens to the FILE, because that is the thing the user has
      // just spent effort on and the thing they will assume is lost.
      footnote: carry?.files
        ? 'We\u2019ll take you to their record with the r\u00e9sum\u00e9 you just uploaded already '
          + 'attached \u2014 save there to keep it. The one already on file is kept too, as an '
          + 'earlier version. Nothing on this form has been filled in.'
        : 'Everything already on their record is kept. Nothing on this form has been filled in.',
    });

    if (!useExisting) return 'blocked';
    acknowledged.current = result.id ?? true;
    // The whole result is handed over, because it carries `editRoute` — the
    // configured path for opening the matched record. The caller must never
    // derive that path; see DuplicateCheckStrategy.EditRoute.
    // `carry` travels through untouched — today the file the user just
    // uploaded, so it arrives with them at the existing record instead of
    // having to be found and attached again.
    onUseExisting?.(result.id, result, reason, carry);
    return 'existing';
  }, [check, config, onUseExisting]);

  return { prompt, check, checking, acknowledgedId: acknowledged };
}
